/**
 * Login — pantalla de autenticación (rediseño GEN-11 según maqueta de Guo:
 * wordmark del tenant, título, identidad delegada como campo subrayado y
 * botón «Entrar»).
 *
 * R-PT-05: Pórtico hace el login y pasa la identidad a R0.
 *          No decide roles ni permisos — eso lo resuelve el Kernel.
 *
 * Modo real: el botón salta al hosted UI de Cognito (Authorization Code +
 * PKCE, src/auth/cognito.js); la SPA nunca ve la contraseña. El primer
 * acceso pide cambiar la contraseña temporal (#243).
 * Modo mock: entra directo contra el descriptor local.
 *
 * Nota de arquitectura: en modo REAL el tenant se resuelve en el bootstrap,
 * DESPUÉS de este paso — antes de entrar no hay tenant conocido (US-58) y se
 * pinta la insignia neutra de plataforma. En modo MOCK la identidad que se
 * delegará ya es conocida (mockLoginPreview, data-driven desde el descriptor
 * mock, sin ramas por tenant): logo, color y usuario se previsualizan aquí.
 * El logo NO viene del kernel: lo resuelve el front por tenant.id (GEN-11,
 * lib/images.js).
 */

import { useEffect } from 'react'
import { useSession } from '../context/SessionContext.jsx'
import { isMock, mockLoginPreview } from '../api/client.js'
import { TenantLogo } from './TenantLogo.jsx'

// GEN-PT-001: sello de versión. Las variables las inyecta deploy-portico.yml
// en el build; en local o mock no existen y se muestra «dev».
function buildStamp() {
  const { VITE_BUILD_SHA: sha, VITE_BUILD_PR: pr, VITE_BUILD_DATE: date } = import.meta.env
  if (!sha) return 'dev'
  const prLabel = /^\d+$/.test(pr ?? '') ? `PR #${pr}` : (pr || 'sin PR')
  return `${prLabel} · ${sha} · ${date}`
}

export default function Login() {
  const { login, loading, error, tenant, user, resolvedTheme } = useSession()
  const brandTenant = tenant ?? mockLoginPreview?.tenant ?? null
  const identity = user ?? mockLoginPreview?.user ?? null

  // Marca data-driven ANTES del bootstrap (solo mock): mismos design tokens y
  // MISMO mecanismo que SessionContext al entrar — la rampa --brand-* se
  // deriva de --color-primary en :root, así que el override vive ahí (un
  // valor scoped no recalcularía las derivadas). En real, branding es null y
  // el login queda neutro de plataforma (US-58).
  const branding = brandTenant?.branding
  useEffect(() => {
    if (!branding) return
    const root = document.documentElement
    if (branding.color_primario) root.style.setProperty('--color-primary', branding.color_primario)
    if (branding.tipografia) root.style.setProperty('--font-body', branding.tipografia)
  }, [branding])

  return (
    <div className="login-root">
      <div className="login-card">
        <TenantLogo tenant={brandTenant} theme={resolvedTheme} height={30} className="login-logo" />

        <h1 className="login-title">Pórtico</h1>
        <p className="login-subtitle">La ventana al gemelo.</p>
        <p className="login-domain">dkagora.ai</p>

        <div className="login-identity">
          <span className="login-identity__label">Identidad delegada · R0</span>
          <span className="login-identity__value">
            {identity?.email ?? identity?.display_name ?? 'Cognito · identidad delegada'}
          </span>
        </div>

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <button
          className="login-btn"
          type="button"
          onClick={login}
          disabled={loading}
        >
          {loading ? 'Entrando…' : 'Entrar'}
        </button>

        <p
          className="login-version"
          data-testid="login-version"
          style={{ marginTop: 12, fontSize: 11, color: '#8a8a8a', textAlign: 'center' }}
        >
          {buildStamp()}
        </p>

        <p className="login-footer">
          {isMock
            ? 'Modo mock · el Pórtico no decide roles; la identidad se delega a R0.'
            : (
              <>
                La identidad se verifica en Cognito y la resuelve el kernel (R-PT-05).{' '}
                ¿Problemas para acceder?{' '}
                <a href="mailto:soporte@dktec.es" className="login-link">
                  Contacta con DKtec
                </a>
              </>
            )}
        </p>
      </div>
    </div>
  )
}
